import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import api from '@crm-src/lib/api'
import type { EmailLayoutType } from '@crm-src/lib/email-layouts'

// Import TargetSelection type to match email-target-selector
export interface EmailRecipient {
  id: string
  name: string
  email: string
  type: 'customer' | 'prospect' | 'manual'
  company?: string
}

export interface TargetSelection {
  recipients: EmailRecipient[]
}

// Campaign recipient (sendout log entry)
export interface CampaignRecipient {
  id: string
  email: string
  name: string
  company?: string
  recipient_type?: string
  is_manual: boolean
  sent_at?: string
  delivered_at?: string
  opened_at?: string
  clicked_at?: string
  bounced_at?: string
  unsubscribed_at?: string
  open_count: number
  click_count: number
  bounce_reason?: string
  status: 'pending' | 'sent' | 'opened' | 'clicked' | 'bounced' | 'unsubscribed'
}

export interface CampaignRecipientsResponse {
  recipients: CampaignRecipient[]
  stats: {
    total: number
    sent: number
    pending: number
    opened: number
    clicked: number
    bounced: number
    unsubscribed: number
  }
  pagination: {
    current_page: number
    per_page: number
    total_count: number
    total_pages: number
  }
}

export interface EmailCampaign {
  id: string
  name: string
  subject: string
  content: string
  content_data?: any  // JSON data for template builders (e.g., newsletter sections)
  status: 'not_sent' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'failed'
  
  // Email layout type for template styling
  layout?: EmailLayoutType
  
  // Legacy field for backward compatibility
  recipient_type: 'prospects' | 'customers' | 'all'
  
  // New flexible targeting field
  target_selection?: TargetSelection
  
  // File attachments
  attachments?: AttachedFile[]

  // Per-campaign sender overrides (blank ⇒ crm-api defaults: marcom@ / tenant
  // company name / reply-to == from). from_address is restricted to a
  // @xopnetworks.com address; reply_to may be any valid email.
  from_address?: string
  from_name?: string
  reply_to?: string

  recipients_count: number
  sent_count: number
  delivered_count: number
  opened_count: number
  clicked_count: number
  bounced_count: number
  unsubscribed_count: number
  open_rate: number
  click_rate: number
  bounce_rate: number
  delivery_rate: number
  created_at: string
  updated_at: string
  scheduled_at?: string
  sent_at?: string
  completed_at?: string
  owner?: {
    id: number
    name: string
    email: string
  }
  created_by?: {
    id: number
    name: string
  }
  updated_by?: {
    id: number
    name: string
  }
}

// Import attachment type
export interface AttachedFile {
  id: string | number
  name: string
  size: number
  type: string
  url?: string
  blob_key?: string
}

interface CampaignStats {
  total_campaigns: number
  not_sent_campaigns: number
  sent_campaigns: number
  total_recipients: number
  average_open_rate: number
  average_click_rate: number
}

interface CampaignsState {
  campaigns: EmailCampaign[]
  stats: CampaignStats | null
  isLoading: boolean
  error: string | null
  lastFetched: number | null
  pagination: {
    current_page: number
    total_pages: number
    total_count: number
    per_page: number
  }
}

const initialState: CampaignsState = {
  campaigns: [],
  stats: null,
  isLoading: false,
  error: null,
  lastFetched: null,
  pagination: {
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 25,
  }
}

// Async thunks for API calls
export const fetchCampaigns = createAsyncThunk(
  'campaigns/fetchCampaigns',
  async (params?: {
    status?: string
    recipient_type?: string
    owner_id?: number
    search?: string
    page?: number
    per_page?: number
    include_stats?: boolean
  }) => {
    try {
      const response = await api.get('/campaigns', { params })
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch campaigns')
    }
  }
)

export const fetchCampaignStats = createAsyncThunk(
  'campaigns/fetchStats',
  async () => {
    try {
      const response = await api.get('/campaigns/stats')
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch campaign stats')
    }
  }
)

export const fetchCampaignById = createAsyncThunk(
  'campaigns/fetchCampaignById',
  async (id: string) => {
    try {
      const response = await api.get(`/campaigns/${id}`)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch campaign')
    }
  }
)

export const createCampaign = createAsyncThunk(
  'campaigns/createCampaign',
  async (campaignData: {
    name?: string
    subject: string
    content: string
    layout?: EmailLayoutType
    target_selection?: TargetSelection
    recipient_type?: 'prospects' | 'customers' | 'all'  // Keep for backward compatibility
    status?: 'not_sent' | 'scheduled'
    scheduled_at?: string
    from_address?: string
    from_name?: string
    reply_to?: string
  }) => {
    try {
      // Transform target_selection to legacy format for API if needed
      let apiData = { ...campaignData }
      
      if (campaignData.target_selection && !campaignData.recipient_type) {
        // For now, determine recipient_type based on recipients
        const recipients = campaignData.target_selection.recipients
        const hasCustomers = recipients.some(r => r.type === 'customer')
        const hasProspects = recipients.some(r => r.type === 'prospect')
        
        if (hasCustomers && hasProspects) {
          apiData.recipient_type = 'all'
        } else if (hasCustomers) {
          apiData.recipient_type = 'customers'
        } else if (hasProspects) {
          apiData.recipient_type = 'prospects'
        } else {
          apiData.recipient_type = 'prospects'
        }
      }
      
      const response = await api.post('/campaigns', { campaign: apiData })
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.errors?.[0] || 'Failed to create campaign')
    }
  }
)

export const updateCampaign = createAsyncThunk(
  'campaigns/updateCampaign',
  async ({ id, data }: { id: string; data: Partial<EmailCampaign> }) => {
    try {
      const response = await api.put(`/campaigns/${id}`, { campaign: data })
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.errors?.[0] || 'Failed to update campaign')
    }
  }
)

export const deleteCampaign = createAsyncThunk(
  'campaigns/deleteCampaign',
  async (id: string) => {
    try {
      await api.delete(`/campaigns/${id}`)
      return { id }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete campaign')
    }
  }
)

export const bulkDeleteCampaigns = createAsyncThunk(
  'campaigns/bulkDeleteCampaigns',
  async (campaignIds: string[]) => {
    try {
      const response = await api.post('/campaigns/bulk_delete', { campaign_ids: campaignIds })
      return { campaignIds, response: response.data }
    } catch (error: any) {
      throw new Error(error.response?.data?.errors?.[0] || 'Failed to delete campaigns')
    }
  }
)

export const scheduleCampaign = createAsyncThunk(
  'campaigns/scheduleCampaign',
  async ({ id, scheduled_at }: { id: string; scheduled_at?: string }) => {
    try {
      const response = await api.post(`/campaigns/${id}/schedule`, { scheduled_at })
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.errors?.[0] || 'Failed to schedule campaign')
    }
  }
)

export const sendCampaignNow = createAsyncThunk(
  'campaigns/sendCampaignNow',
  async (id: string) => {
    try {
      // Use longer timeout for synchronous email sending (5 minutes)
      // Since we're temporarily sending emails directly without queue
      const response = await api.post(`/campaigns/${id}/send_now`, null, {
        timeout: 300000  // 5 minutes timeout for large campaigns
      })
      return response.data
    } catch (error: any) {
      // Handle timeout specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('Sending emails is taking longer than expected. Please check the campaign status.')
      }
      // Keep crm-api's machine-readable code (e.g. ALL_RECIPIENTS_UNSUBSCRIBED)
      // on the error — createAsyncThunk serializes a string `code` through
      // unwrap(), so the page can offer "Resubscribe & send".
      const failure = new Error(error.response?.data?.errors?.[0] || 'Failed to send campaign') as Error & { code?: string }
      if (typeof error.response?.data?.code === 'string') failure.code = error.response.data.code
      throw failure
    }
  }
)

/**
 * Put the audience members who unsubscribed back on the list (super admin
 * only; crm-api refuses more than a handful). Offered when Send Now finds
 * nobody to email because everyone in the audience opted out.
 */
export const resubscribeCampaignAudience = async (id: string): Promise<string[]> => {
  try {
    const response = await api.post(`/campaigns/${id}/resubscribe_audience`)
    return response.data?.resubscribed ?? []
  } catch (error: any) {
    throw new Error(error.response?.data?.errors?.[0] || 'Failed to resubscribe')
  }
}

export const pauseCampaign = createAsyncThunk(
  'campaigns/pauseCampaign',
  async (id: string) => {
    try {
      const response = await api.post(`/campaigns/${id}/pause`)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.errors?.[0] || 'Failed to pause campaign')
    }
  }
)

export const resumeCampaign = createAsyncThunk(
  'campaigns/resumeCampaign',
  async (id: string) => {
    try {
      const response = await api.post(`/campaigns/${id}/resume`)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.errors?.[0] || 'Failed to resume campaign')
    }
  }
)

export const duplicateCampaign = createAsyncThunk(
  'campaigns/duplicateCampaign',
  async (id: string) => {
    try {
      const response = await api.post(`/campaigns/${id}/duplicate`)
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.errors?.[0] || 'Failed to duplicate campaign')
    }
  }
)

// Fetch campaign recipients (sendout logs)
export const fetchCampaignRecipients = createAsyncThunk(
  'campaigns/fetchCampaignRecipients',
  async (params: {
    campaignId: string
    status?: string
    search?: string
    sort_by?: string
    sort_dir?: 'asc' | 'desc'
    page?: number
    per_page?: number
  }) => {
    try {
      const { campaignId, ...queryParams } = params
      const response = await api.get(`/campaigns/${campaignId}/recipients`, { params: queryParams })
      return response.data as CampaignRecipientsResponse
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch campaign recipients')
    }
  }
)

const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearCache: (state) => {
      state.campaigns = []
      state.stats = null
      state.lastFetched = null
      state.pagination = initialState.pagination
    },
    optimisticUpdateCampaign: (state, action: PayloadAction<{ id: string; updates: Partial<EmailCampaign> }>) => {
      const { id, updates } = action.payload
      const campaignIndex = state.campaigns.findIndex(campaign => campaign.id === id)
      if (campaignIndex !== -1) {
        state.campaigns[campaignIndex] = { ...state.campaigns[campaignIndex], ...updates }
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch campaigns
    builder
      .addCase(fetchCampaigns.pending, (state) => {
        // Only show loading skeleton on initial load (no campaigns yet)
        // Background polls/refetches should not flash the skeleton
        if (state.campaigns.length === 0) {
          state.isLoading = true
        }
        state.error = null
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.isLoading = false
        state.campaigns = action.payload.campaigns || []
        if (action.payload.stats) {
          state.stats = action.payload.stats
        }
        state.lastFetched = Date.now()
        // Update pagination if provided
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination
        }
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch campaigns'
      })
    
    // Fetch stats
    builder
      .addCase(fetchCampaignStats.pending, (state) => {
        // Don't set loading for stats only
        state.error = null
      })
      .addCase(fetchCampaignStats.fulfilled, (state, action) => {
        state.stats = action.payload
      })
      .addCase(fetchCampaignStats.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch campaign stats'
      })

    // Fetch single campaign by ID
    builder
      .addCase(fetchCampaignById.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchCampaignById.fulfilled, (state, action) => {
        state.isLoading = false
        if (action.payload.campaign) {
          // Add or update the campaign in the store
          const index = state.campaigns.findIndex(c => c.id === action.payload.campaign.id)
          if (index >= 0) {
            state.campaigns[index] = action.payload.campaign
          } else {
            state.campaigns.push(action.payload.campaign)
          }
        }
      })
      .addCase(fetchCampaignById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch campaign'
      })

    // Create campaign
    builder
      .addCase(createCampaign.pending, (state) => {
        state.error = null
      })
      .addCase(createCampaign.fulfilled, (state, action) => {
        if (action.payload.campaign) {
          state.campaigns.unshift(action.payload.campaign)
          // Update stats if available
          if (state.stats) {
            state.stats.total_campaigns += 1
            if (action.payload.campaign.status === 'not_sent') {
              state.stats.not_sent_campaigns += 1
            }
          }
        }
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create campaign'
      })

    // Update campaign
    builder
      .addCase(updateCampaign.pending, (state) => {
        state.error = null
      })
      .addCase(updateCampaign.fulfilled, (state, action) => {
        if (action.payload.campaign) {
          const index = state.campaigns.findIndex(campaign => campaign.id === action.payload.campaign.id)
          if (index !== -1) {
            state.campaigns[index] = action.payload.campaign
          }
        }
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update campaign'
      })

    // Delete campaign
    builder
      .addCase(deleteCampaign.pending, (state) => {
        state.error = null
      })
      .addCase(deleteCampaign.fulfilled, (state, action) => {
        const id = action.payload.id
        state.campaigns = state.campaigns.filter(campaign => campaign.id !== id)
        // Update stats
        if (state.stats) {
          state.stats.total_campaigns = Math.max(0, state.stats.total_campaigns - 1)
        }
      })
      .addCase(deleteCampaign.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete campaign'
      })

    // Bulk delete campaigns
    builder
      .addCase(bulkDeleteCampaigns.pending, (state) => {
        state.error = null
      })
      .addCase(bulkDeleteCampaigns.fulfilled, (state, action) => {
        const { campaignIds } = action.payload
        state.campaigns = state.campaigns.filter(campaign => !campaignIds.includes(campaign.id))
        // Update stats
        if (state.stats) {
          state.stats.total_campaigns = Math.max(0, state.stats.total_campaigns - campaignIds.length)
        }
      })
      .addCase(bulkDeleteCampaigns.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to bulk delete campaigns'
      })

    // Schedule campaign
    builder
      .addCase(scheduleCampaign.fulfilled, (state, action) => {
        if (action.payload.campaign) {
          const index = state.campaigns.findIndex(campaign => campaign.id === action.payload.campaign.id)
          if (index !== -1) {
            state.campaigns[index] = action.payload.campaign
          }
        }
      })
      .addCase(scheduleCampaign.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to schedule campaign'
      })

    // Send campaign now
    builder
      .addCase(sendCampaignNow.fulfilled, (state, action) => {
        if (action.payload.campaign) {
          const index = state.campaigns.findIndex(campaign => campaign.id === action.payload.campaign.id)
          if (index !== -1) {
            state.campaigns[index] = action.payload.campaign
            // Update stats
            if (state.stats && action.payload.campaign.status === 'sent') {
              state.stats.sent_campaigns += 1
              if (state.campaigns[index].status === 'not_sent') {
                state.stats.not_sent_campaigns = Math.max(0, state.stats.not_sent_campaigns - 1)
              }
            }
          }
        }
      })
      .addCase(sendCampaignNow.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to send campaign'
      })

    // Pause campaign
    builder
      .addCase(pauseCampaign.fulfilled, (state, action) => {
        if (action.payload.campaign) {
          const index = state.campaigns.findIndex(campaign => campaign.id === action.payload.campaign.id)
          if (index !== -1) {
            state.campaigns[index] = action.payload.campaign
          }
        }
      })
      .addCase(pauseCampaign.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to pause campaign'
      })

    // Resume campaign
    builder
      .addCase(resumeCampaign.fulfilled, (state, action) => {
        if (action.payload.campaign) {
          const index = state.campaigns.findIndex(campaign => campaign.id === action.payload.campaign.id)
          if (index !== -1) {
            state.campaigns[index] = action.payload.campaign
          }
        }
      })
      .addCase(resumeCampaign.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to resume campaign'
      })

    // Duplicate campaign
    builder
      .addCase(duplicateCampaign.fulfilled, (state, action) => {
        if (action.payload.campaign) {
          state.campaigns.unshift(action.payload.campaign)
          // Update stats
          if (state.stats) {
            state.stats.total_campaigns += 1
            state.stats.not_sent_campaigns += 1
          }
        }
      })
      .addCase(duplicateCampaign.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to duplicate campaign'
      })
  },
})

export const { clearError, clearCache, optimisticUpdateCampaign } = campaignsSlice.actions

export default campaignsSlice.reducer





